/**
 * escposItemsTable.ts — Tabla de productos térmica (58/80 mm).
 * Rejilla reutilizable `+---+` / `|…|` (misma familia que cierre y movimientos).
 * Columnas: (#) | Cant. | Producto | Total
 */
import {
  blockHeaderTotal,
  formatTicketSublineAmount,
  isMenuGroupBlock,
  resolveTicketBlockUnitPrice,
  stripTicketSubLineName,
  splitTicketVariantSummary,
  ticketSubLineLineTotal,
  TICKET_UNIT_PRICE_LABEL,
} from "@/features/caja/utils/ticketItemBlocks";
import {
  gridLineSep,
  gridRowCells,
  padCol,
  sanitizeForThermal,
  TICKET_SUBLINE_PREFIX,
  truncCell,
} from "@/features/caja/utils/escposLayout";
import {
  isNarrowTicket,
  resolveThermalGridWidths,
} from "@/features/caja/utils/ticketCierreAsciiLayout";
import { formatTicketSkuLine } from "@/features/caja/utils/ticketLineBreakdown";
import type { TicketItem } from "@/features/caja/components/TicketItemsTable";

export interface EscPosTableItem {
  nombre: string;
  cantidad: number;
  precioUnitario: number;
  /** Precio de lista unitario original (referencia: P.UNIT "antes" en térmica). */
  precioUnitarioOriginal?: number;
  peso?: number;
  precioPorKilo?: number;
  lineSubtotal?: number;
  skuResumen?: string;
  variantesResumen?: string;
  modificadoresResumen?: string;
  isGroupHeader?: boolean;
  isSubLine?: boolean;
  isGroupFooter?: boolean;
  footerKind?: "inventoryVariants" | "orderCustomization";
  opcionesResumen?: string;
  isDiscountLine?: boolean;
  isArticleTotalLine?: boolean;
  isTaxLine?: boolean;
  taxRatePct?: number;
  /** Descuento de precio de la línea (mayorista / cliente VIP): fila con valor. */
  esDescuentoPrecioLine?: boolean;
}

interface RenderOpts {
  items: EscPosTableItem[];
  paperWidthMm: 58 | 80;
  emitLine: (s: string) => void;
  bold?: (on: boolean) => void;
  money: (n: number) => string;
  wrap: (s: string, n: number) => string[];
  showColumnHeader?: boolean;
  thermal?: boolean;
  /** Enumera cada producto con una columna `#I` (solo ticket de venta). */
  showIndex?: boolean;
}

interface TableCtx {
  widths: number[];
  useIdx: boolean;
  totalWidth: number;
  emitLine: (s: string) => void;
  money: (n: number) => string;
  wrap: (s: string, n: number) => string[];
  txt: (s: string) => string;
  bold?: (on: boolean) => void;
}

/**
 * Convierte filas de ticket (`TicketItem`, fuente única web+térmica) a filas de
 * la rejilla ESC/POS. Misma estructura de "Detalle de venta" (Cantidad |
 * Producto | Total, con SKU, P.UNIT y variantes) compartida por el ticket de
 * venta y la etiqueta del pedido: evita duplicar el mapeo.
 */
export function toEscPosTableItems(
  ticketItems: TicketItem[],
): EscPosTableItem[] {
  return ticketItems.map((it) => ({
    nombre: it.nombre,
    cantidad: it.cantidad,
    precioUnitario: it.precioUnitario,
    precioUnitarioOriginal: it.precioUnitarioOriginal,
    peso: it.peso,
    precioPorKilo: it.precioPorKilo,
    lineSubtotal: it.lineSubtotal,
    skuResumen: it.skuResumen,
    variantesResumen: it.isGroupFooter
      ? it.opcionesResumen
      : [it.variantesResumen, it.modificadoresResumen, it.opcionesResumen]
          .filter((s) => s?.trim())
          .join(" · ") || undefined,
    isGroupHeader: it.isGroupHeader,
    isSubLine: it.isSubLine,
    isGroupFooter: it.isGroupFooter,
    footerKind: it.footerKind,
    isDiscountLine: it.isDiscountLine,
    isArticleTotalLine: it.isArticleTotalLine,
    isTaxLine: it.isTaxLine,
    taxRatePct: it.taxRatePct,
    esDescuentoPrecioLine: it.esDescuentoPrecioLine,
  }));
}

function padQtyCell(text: string, width: number): string {
  const t = (text || "").trim();
  if (!t) return " ".repeat(width);
  if (t.length > width) return truncCell(t, width);
  return padCol(t, width, "center");
}

function padEmptyCell(width: number): string {
  return " ".repeat(Math.max(0, width));
}

function headerLabel(full: string, narrow: string, width: number): string {
  const label =
    full.length <= width
      ? full
      : narrow.length <= width
        ? narrow
        : narrow.slice(0, width);
  return padCol(label, width, "center");
}

function padMoneyCell(text: string, width: number): string {
  const t = (text || "").trim();
  if (t.length <= width) return " ".repeat(width - t.length) + t;
  return t.slice(0, width);
}

/** Pesos de columnas sin enumeración (Cant. | Producto | Total). */
function ventaItemsColWeights(totalWidth: number): number[] {
  return isNarrowTicket(totalWidth) ? [4, 13, 11] : [8, 25, 11];
}

/** Celda del índice de producto (#I) alineada a la derecha. */
function idxCellText(index: number | null, width: number): string {
  const t = index == null ? "" : String(index);
  if (t.length >= width) return t;
  return " ".repeat(width - t.length) + t;
}

/**
 * Sublíneas: desde el borde izquierdo del contenido hasta la columna Total.
 * La celda izquierda abarca todas las columnas menos la última.
 */
function ventaSublineWidths(widths: number[]): [number, number] {
  const n = widths.length;
  const labelWidth = widths.slice(0, -1).reduce((a, b) => a + b, 0) + (n - 2);
  return [labelWidth, widths[n - 1]];
}

/** Texto de sublínea desde el borde izquierdo (sin | tras Cantidad). */
function sublineLabelCell(text: string, labelWidth: number): string {
  return truncCell(text, labelWidth);
}

function groupBlocks(items: EscPosTableItem[]): EscPosTableItem[][] {
  const blocks: EscPosTableItem[][] = [];
  let i = 0;
  while (i < items.length) {
    const item = items[i];
    if (item.isGroupHeader) {
      const block: EscPosTableItem[] = [item];
      i += 1;
      while (
        i < items.length &&
        (items[i].isSubLine ||
          items[i].isGroupFooter ||
          items[i].isDiscountLine ||
          items[i].isArticleTotalLine ||
          items[i].isTaxLine)
      ) {
        block.push(items[i]);
        i += 1;
      }
      blocks.push(block);
    } else if (item.isDiscountLine || item.isArticleTotalLine) {
      i += 1;
    } else {
      const block: EscPosTableItem[] = [item];
      i += 1;
      while (
        i < items.length &&
        (items[i].isSubLine ||
          items[i].isTaxLine ||
          items[i].isDiscountLine ||
          items[i].isArticleTotalLine)
      ) {
        block.push(items[i]);
        i += 1;
      }
      blocks.push(block);
    }
  }
  return blocks;
}

function formatPesoQtyLabel(peso: number): string {
  return `${peso.toFixed(3)}kg`;
}

/** Número compacto del peso para celdas estrechas (58mm). */
function compactPesoQtyLabel(peso: number, width: number): string {
  const t = peso.toFixed(3);
  return t.length <= width ? t : t.slice(0, width);
}

function prependUnitPriceSubline(
  header: EscPosTableItem,
  priced: Array<{ label: string; amount: string }>,
  money: (n: number) => string,
): void {
  const unitPrice = resolveTicketBlockUnitPrice(header);
  if (unitPrice != null) {
    const qty = Math.max(1, header.cantidad || 1);
    const label =
      (header.taxRatePct ?? 0) > 0
        ? `${TICKET_UNIT_PRICE_LABEL} (sin IVA)`
        : TICKET_UNIT_PRICE_LABEL;
    const amount = qty > 1 ? `${qty}x${money(unitPrice)}` : money(unitPrice);
    priced.unshift({ label, amount });
  }
}

function skuContinuationLines(
  sku: string | undefined,
  txt: (s: string) => string,
): string[] {
  const trimmed = sku?.trim();
  return trimmed ? [txt(formatTicketSkuLine(trimmed))] : [];
}

function emitSubTextLines(
  ctx: TableCtx,
  text: string,
  prefix = TICKET_SUBLINE_PREFIX,
): void {
  const { widths } = ctx;
  const [labelWidth, totalWidth] = ventaSublineWidths(widths);
  const bodyWidth = Math.max(1, labelWidth - prefix.length);
  const lines = ctx.wrap(text, bodyWidth);
  for (let i = 0; i < lines.length; i++) {
    const content =
      i === 0
        ? `${prefix}${lines[i]}`
        : `${" ".repeat(prefix.length)}${lines[i]}`;
    ctx.emitLine(
      gridRowCells(
        [sublineLabelCell(content, labelWidth), padEmptyCell(totalWidth)],
        [labelWidth, totalWidth],
      ),
    );
  }
}

/**
 * Fila principal de la rejilla. Con índice (#I) la primera celda es el número
 * del producto; el monto va SIEMPRE completo en la última columna.
 *
 * Regla híbrida: mientras todas las palabras quepan en la columna Producto, se
 * mantiene la rejilla con sus 4 separadores `|` en TODAS las filas (nombre,
 * continuación y SKU). Solo cuando una PALABRA es más larga que la columna
 * (caso 58mm tipo «Limpiaparabrisas») esa fila usa la franja completa
 * (`#+Cant+Producto` fusionada) para no partir la palabra a mitad.
 */
function emitGridDataRow(
  ctx: TableCtx,
  index: number | null,
  qty: string,
  name: string,
  total: string,
  nameContinuations: string[] = [],
): void {
  const { widths, useIdx, emitLine, wrap, bold } = ctx;
  const qtyW = useIdx ? widths[1] : widths[0];
  const nameW = useIdx ? widths[2] : widths[1];
  const totalW = widths[widths.length - 1];
  const idxW = useIdx ? widths[0] : 0;
  const [labelW] = ventaSublineWidths(widths);

  const qtyCell = padQtyCell(qty, qtyW);
  const mainCells = (
    idx: number | null,
    q: string,
    n0: string,
    totalCell: string,
  ): string[] => {
    if (useIdx) {
      return [idxCellText(idx, idxW), q, truncCell(n0, nameW), totalCell];
    }
    return [q, truncCell(n0, nameW), totalCell];
  };

  // ¿Alguna palabra del nombre desborda la columna Producto?
  const longWord = name.split(/\s+/).some((w) => w.length > nameW);

  if (!longWord) {
    // ── Rejilla estricta: 4 columnas con `|` en todas las filas ─────────────
    const nameLines = wrap(name, nameW);
    const continuationRows: string[] = [];
    for (let i = 1; i < nameLines.length; i++) {
      continuationRows.push(nameLines[i]);
    }
    for (const continuation of nameContinuations) {
      for (const line of wrap(continuation, nameW)) {
        continuationRows.push(line);
      }
    }

    const deferTotal = continuationRows.length > 0;
    bold?.(true);
    emitLine(
      gridRowCells(
        mainCells(
          index,
          qtyCell,
          nameLines[0] ?? "",
          deferTotal ? padEmptyCell(totalW) : padMoneyCell(total, totalW),
        ),
        widths,
      ),
    );
    bold?.(false);

    for (let i = 0; i < continuationRows.length; i++) {
      const isLast = i === continuationRows.length - 1;
      const totalCell =
        isLast && deferTotal
          ? padMoneyCell(total, totalW)
          : padEmptyCell(totalW);
      const cells = useIdx
        ? [
            idxCellText(null, idxW),
            padEmptyCell(qtyW),
            truncCell(continuationRows[i], nameW),
            totalCell,
          ]
        : [
            padEmptyCell(qtyW),
            truncCell(continuationRows[i], nameW),
            totalCell,
          ];
      emitLine(gridRowCells(cells, widths));
    }
    return;
  }

  // ── Palabra más larga que la columna: franja completa (sin partir) ───────
  const continuations = [
    ...(name.length > nameW ? wrap(name, labelW - 2) : [name]),
  ];
  for (const continuation of nameContinuations) {
    for (const line of wrap(continuation, labelW - 2)) {
      continuations.push(line);
    }
  }

  bold?.(true);
  emitLine(
    gridRowCells(mainCells(index, qtyCell, "", padEmptyCell(totalW)), widths),
  );
  bold?.(false);

  const padLabel = (text: string) =>
    text.length >= labelW ? text : text.padEnd(labelW, " ");
  for (let i = 0; i < continuations.length; i++) {
    const isLast = i === continuations.length - 1;
    emitLine(
      gridRowCells(
        [
          sublineLabelCell(padLabel(`  ${continuations[i]}`), labelW),
          isLast ? padMoneyCell(total, totalW) : padEmptyCell(totalW),
        ],
        [labelW, totalW],
      ),
    );
  }
}

function emitPricedSubline(ctx: TableCtx, label: string, amount: string): void {
  const { widths } = ctx;
  const [labelWidth, totalWidth] = ventaSublineWidths(widths);
  const fullLabel = `${TICKET_SUBLINE_PREFIX}${label}`;
  const labelLines = ctx.wrap(fullLabel, labelWidth);

  if (labelLines.length <= 1) {
    ctx.emitLine(
      gridRowCells(
        [
          sublineLabelCell(labelLines[0] ?? fullLabel, labelWidth),
          padMoneyCell(amount, totalWidth),
        ],
        [labelWidth, totalWidth],
      ),
    );
    return;
  }

  ctx.emitLine(
    gridRowCells(
      [
        sublineLabelCell(labelLines[0], labelWidth),
        padMoneyCell(amount, totalWidth),
      ],
      [labelWidth, totalWidth],
    ),
  );
  for (let i = 1; i < labelLines.length; i++) {
    ctx.emitLine(
      gridRowCells(
        [sublineLabelCell(labelLines[i], labelWidth), padEmptyCell(totalWidth)],
        [labelWidth, totalWidth],
      ),
    );
  }
}

function emitItemSublines(
  ctx: TableCtx,
  priced: Array<{ label: string; amount: string }>,
  text: string[],
): void {
  if (priced.length === 0 && text.length === 0) return;

  ctx.emitLine(gridLineSep(ctx.widths));

  for (const row of priced) {
    emitPricedSubline(ctx, row.label, row.amount);
  }
  for (const line of text) {
    emitSubTextLines(ctx, line);
  }
}

function pushTicketSublineRows(
  ctx: TableCtx,
  it: EscPosTableItem,
  priced: Array<{ label: string; amount: string }>,
  ratePct = 0,
  qty = 1,
): void {
  if (it.isTaxLine) {
    priced.push({
      label: ctx.txt(it.nombre),
      amount: `+${ctx.money(Math.max(0, Math.round(it.lineSubtotal ?? 0)))}`,
    });
    return;
  }
  if (it.esDescuentoPrecioLine) {
    const divisor = ratePct > 0 ? 1 + ratePct / 100 : 1;
    const neto = Math.round((it.lineSubtotal ?? 0) / divisor);
    const monto = Math.abs(neto);
    const baseLabel = it.nombre || "";
    const esUnitario = neto > 0;
    const label = ctx.txt(
      ratePct > 0 && esUnitario ? `${baseLabel} (sin IVA)` : baseLabel,
    );
    const amount =
      esUnitario && qty > 1
        ? `${qty}x${ctx.money(monto)}`
        : neto < 0
          ? `-${ctx.money(monto)}`
          : ctx.money(monto);
    priced.push({ label, amount });
    return;
  }
  if (it.isDiscountLine) {
    priced.push({
      label: ctx.txt(it.nombre),
      amount: `-${ctx.money(Math.abs(Math.round(it.lineSubtotal ?? 0)))}`,
    });
    return;
  }
  if (it.isArticleTotalLine) {
    priced.push({
      label: ctx.txt(it.nombre),
      amount: ctx.money(Math.round(it.lineSubtotal ?? 0)),
    });
  }
}

function renderSimpleBlock(
  block: EscPosTableItem[],
  ctx: TableCtx,
  index: number | null,
): void {
  const { widths } = ctx;
  const it = block[0];
  if (!it) return;

  const priced: Array<{ label: string; amount: string }> = [];
  const text: string[] = [];

  if (it.isDiscountLine || it.isArticleTotalLine || it.isTaxLine) {
    for (const row of block) {
      pushTicketSublineRows(ctx, row, priced);
    }
    emitItemSublines(ctx, priced, text);
    return;
  }
  const narrow = isNarrowTicket(ctx.totalWidth);
  const isPeso = !!it.peso && it.peso > 0;
  const qtyW = ctx.useIdx ? widths[1] : widths[0];
  const pesoLabel = formatPesoQtyLabel(it.peso || 0);
  // En 58mm la celda de cantidad es corta: se muestra el número del peso y la
  // unidad va en la fila de detalle (nunca «3.0.», nunca se pierde la unidad).
  const qtyLabel =
    isPeso && pesoLabel.length > qtyW && narrow
      ? compactPesoQtyLabel(it.peso || 0, qtyW)
      : isPeso
        ? pesoLabel
        : String(it.cantidad);
  const lineTotal =
    it.lineSubtotal != null
      ? Math.round(it.lineSubtotal)
      : isPeso
        ? Math.round((it.peso || 0) * (it.precioPorKilo || 0)) ||
          it.precioUnitario
        : it.precioUnitario * it.cantidad;

  emitGridDataRow(
    ctx,
    index,
    qtyLabel,
    ctx.txt(it.nombre),
    ctx.money(lineTotal),
    skuContinuationLines(it.skuResumen, ctx.txt),
  );

  // Producto por peso: el precio por kilo va con su valor en la columna Total
  // (igual que el P.UNIT de los productos por unidad), antes del IVA.
  const ppk = isPeso ? it.precioPorKilo || 0 : 0;
  if (isPeso && ppk > 0) {
    priced.push({
      label: ctx.txt("Precio por kg"),
      amount: `${ctx.money(ppk)}/kg`,
    });
  }
  if (isPeso && pesoLabel.length > qtyW && narrow) {
    text.push(`Peso: ${pesoLabel}`);
  }

  if (it.variantesResumen?.trim()) {
    for (const part of splitTicketVariantSummary(it.variantesResumen)) {
      text.push(ctx.txt(part));
    }
  }
  if (it.modificadoresResumen?.trim()) {
    text.push(ctx.txt(it.modificadoresResumen));
  }

  for (const sub of block.slice(1)) {
    pushTicketSublineRows(ctx, sub, priced, it.taxRatePct ?? 0, it.cantidad);
  }

  prependUnitPriceSubline(it, priced, ctx.money);

  emitItemSublines(ctx, priced, text);
}

function renderExpandedBlock(
  block: EscPosTableItem[],
  ctx: TableCtx,
  index: number | null,
): void {
  const { widths } = ctx;
  const header = block[0];
  const headerTotal = blockHeaderTotal(header);
  const headerName = ctx.txt(header.nombre);
  const menu = isMenuGroupBlock(block);
  const priced: Array<{ label: string; amount: string }> = [];
  const text: string[] = [];

  const qtyW = ctx.useIdx ? widths[1] : widths[0];
  const narrow = isNarrowTicket(ctx.totalWidth);
  const isPeso = !!header.peso && header.peso > 0;
  const pesoLabel = formatPesoQtyLabel(header.peso || 0);
  const qtyLabel =
    isPeso && pesoLabel.length > qtyW && narrow
      ? compactPesoQtyLabel(header.peso || 0, qtyW)
      : isPeso
        ? pesoLabel
        : String(header.cantidad || 1);

  // Los hubs de adiciones se listan como un ítem más (cantidad 1), igual que
  // los hubs de menú: celda de cantidad nunca vacía.
  emitGridDataRow(ctx, index, qtyLabel, headerName, ctx.money(headerTotal), [
    ...skuContinuationLines(header.skuResumen, ctx.txt),
  ]);

  if (isPeso && pesoLabel.length > qtyW && narrow) {
    text.push(`Peso: ${pesoLabel}`);
  }

  for (const it of block.slice(1)) {
    if (it.isGroupFooter) {
      const footerText = (it.opcionesResumen ?? it.variantesResumen)?.trim();
      if (!footerText) continue;
      const body = ctx.txt(footerText);
      if (it.footerKind === "orderCustomization") {
        text.push(`[${body}]`);
      } else {
        for (const part of splitTicketVariantSummary(body)) {
          text.push(part);
        }
      }
      continue;
    }

    if (it.isDiscountLine) {
      priced.push({
        label: ctx.txt(it.nombre),
        amount: `-${ctx.money(Math.abs(Math.round(it.lineSubtotal ?? 0)))}`,
      });
      continue;
    }

    if (it.isArticleTotalLine) {
      priced.push({
        label: ctx.txt(it.nombre),
        amount: ctx.money(Math.round(it.lineSubtotal ?? 0)),
      });
      continue;
    }

    if (it.isTaxLine) {
      priced.push({
        label: ctx.txt(it.nombre),
        amount: ctx.money(Math.max(0, Math.round(it.lineSubtotal ?? 0))),
      });
      continue;
    }

    if (!it.isSubLine) continue;

    const subName = stripTicketSubLineName(ctx.txt(it.nombre));
    const lineTotal = ticketSubLineLineTotal(it);

    if (lineTotal !== 0) {
      priced.push({
        label: subName,
        amount: formatTicketSublineAmount(lineTotal, ctx.money),
      });
    } else {
      text.push(subName);
    }
  }

  if (!menu) {
    prependUnitPriceSubline(header, priced, ctx.money);
  }

  emitItemSublines(ctx, priced, text);
}

/** Imprime la tabla de productos con rejilla térmica reutilizable. */
export function renderItemsTableEscPos(opts: RenderOpts): void {
  const { items, paperWidthMm, emitLine, bold, money, wrap } = opts;
  const thermal = opts.thermal !== false;
  const txt = (s: string) => (thermal ? sanitizeForThermal(s) : s);
  const totalWidth = paperWidthMm === 80 ? 48 : 32;
  const useIdx = opts.showIndex === true;
  const narrow = isNarrowTicket(totalWidth);

  const blocks = groupBlocks(items);
  const productCount = blocks.filter((b) => {
    const f = b[0];
    return Boolean(
      f && !f.isDiscountLine && !f.isArticleTotalLine && !f.isTaxLine,
    );
  }).length;
  const idxW = useIdx
    ? Math.max(2, Math.min(3, String(productCount).length))
    : 0;

  /** Columnas con enumeración: # | Cant | Producto | Total (ancho exacto). */
  const indexedColWidths = (): number[] => {
    const inner = totalWidth - 5; // 5 bordes `|`+`+` para 4 columnas
    const money = isNarrowTicket(totalWidth) ? 11 : 12;
    // En 58mm la cantidad es 4 (el peso con decimales sale con su nota
    // «Peso: X kg»); así el nombre gana una columna más.
    const qty = isNarrowTicket(totalWidth) ? 4 : 7;
    const prod = Math.max(8, inner - idxW - qty - money);
    return [idxW, qty, prod, money];
  };

  const widths = useIdx
    ? indexedColWidths()
    : resolveThermalGridWidths(totalWidth, ventaItemsColWeights(totalWidth));

  const ctx: TableCtx = {
    widths,
    useIdx,
    totalWidth,
    emitLine,
    money,
    wrap,
    txt,
    bold,
  };

  if (opts.showColumnHeader !== false) {
    bold?.(true);
    emitLine(gridLineSep(widths));
    if (useIdx) {
      const h = narrow
        ? ["#", "Cant", "Prod", "Tot"]
        : ["#", "Cantidad", "Producto", "Total"];
      emitLine(
        gridRowCells(
          h.map((label, i) => headerLabel(label, label, widths[i])),
          widths,
        ),
      );
    } else {
      emitLine(
        gridRowCells(
          narrow
            ? [
                headerLabel("Cant.", "Cant.", widths[0]),
                headerLabel("Prod.", "Prod.", widths[1]),
                headerLabel("Tot.", "Tot.", widths[2]),
              ]
            : [
                headerLabel("Cantidad", "Cant.", widths[0]),
                headerLabel("Producto", "Prod.", widths[1]),
                headerLabel("Total", "Tot.", widths[2]),
              ],
          widths,
        ),
      );
    }
    bold?.(false);
    emitLine(gridLineSep(widths));
  }

  let index = 0;
  blocks.forEach((block) => {
    const first = block[0];
    if (
      first &&
      (first.isDiscountLine || first.isArticleTotalLine || first.isTaxLine)
    ) {
      if (first.isGroupHeader) renderExpandedBlock(block, ctx, null);
      else renderSimpleBlock(block, ctx, null);
    } else {
      index += 1;
      if (first?.isGroupHeader) renderExpandedBlock(block, ctx, index);
      else renderSimpleBlock(block, ctx, index);
    }
    emitLine(gridLineSep(widths));
  });
}
